import { EMPTY_DRAFT_STATE, type DraftSessionState } from './types';

/**
 * Persist draft-mode state inside Office.context.document.settings so a
 * user who starts a draft, closes Word, and reopens the same document can
 * resume mid-QnA without losing their brief / template / answers.
 *
 * We key separately from the edit-mode state (`nyayassist:wordEditState`)
 * because draft sessions are independent of the Word doc's edit history —
 * the generated DOCX *replaces* the current document content on completion.
 */
const KEY = 'nyayassist:draftState';

export function getDraftState(): DraftSessionState {
  const raw = Office.context.document.settings.get(KEY) as unknown;
  if (!raw || typeof raw !== 'object') return { ...EMPTY_DRAFT_STATE };
  const r = raw as Partial<DraftSessionState>;
  return {
    chatId: r.chatId ?? null,
    featureId: r.featureId ?? null,
    phase: r.phase ?? 'idle',
    prompt: r.prompt ?? '',
    template: Array.isArray(r.template) ? r.template : [],
    questions: Array.isArray(r.questions) ? r.questions : [],
    draftS3Key: r.draftS3Key ?? null,
    draftExperience:
      r.draftExperience === 'instant' || r.draftExperience === 'interactive'
        ? r.draftExperience
        : 'interactive',
    processingMode:
      r.processingMode === 'speed' || r.processingMode === 'accuracy'
        ? r.processingMode
        : 'speed',
    updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : 0,
  };
}

export async function setDraftState(patch: Partial<DraftSessionState>): Promise<void> {
  const current = getDraftState();
  const next: DraftSessionState = { ...current, ...patch, updatedAt: Date.now() };
  Office.context.document.settings.set(KEY, next);
  await persist();
}

export async function clearDraftState(): Promise<void> {
  Office.context.document.settings.set(KEY, null);
  await persist();
}

function persist(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message ?? 'settings.saveAsync failed'));
        return;
      }
      resolve();
    });
  });
}
